// SmartFuel homepage sections, October 2026 (EVA-7061).
// <sf-buy-box>: pack and plan picker for sections/sf-smart-buy.liquid.
// <sf-see-it>: video whose chapters follow along, for sections/sf-see-it.liquid.
// Loaded only by those sections, so a hidden section loads nothing.

const reduceMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

// Marks a section once it scrolls into view, for one-off entrance animation.
function markSeen(el) {
  if (!('IntersectionObserver' in window)) {
    el.setAttribute('data-seen', '');
    return;
  }
  const io = new IntersectionObserver(
    (entries) => {
      if (entries.some((e) => e.isIntersecting)) {
        el.setAttribute('data-seen', '');
        io.disconnect();
      }
    },
    { threshold: 0.3 }
  );
  io.observe(el);
}

if (!customElements.get('sf-buy-box')) {
  customElements.define(
    'sf-buy-box',
    class extends HTMLElement {
      connectedCallback() {
        if (this._ready) return;
        this._ready = true;
        try {
          this.variants = JSON.parse(this.querySelector('[data-sf-variants]').textContent);
        } catch (e) {
          return;
        }
        this.form = this.querySelector('form');
        this.idInput = this.querySelector('[name="id"]');
        this.planInput = this.querySelector('[name="selling_plan"]');
        this.stage = this.querySelector('[data-sf-stage]');
        this.button = this.querySelector('[data-sf-add]');
        this.status = this.querySelector('[data-sf-status]');

        this.addEventListener('change', () => this.update(true));
        this.form.addEventListener('submit', (e) => this.add(e));
        this.update(false);

        const section = this.closest('.sf-buy');
        if (section) markSeen(section);
      }

      get variant() {
        const id = (this.querySelector('[name="sf-pack"]:checked') || {}).value;
        return this.variants.find((v) => String(v.id) === String(id)) || this.variants[0];
      }

      get plan() {
        return (this.querySelector('[name="sf-plan"]:checked') || {}).value || 'once';
      }

      get frequency() {
        return (this.querySelector('[name="sf-freq"]:checked') || {}).value || '';
      }

      update(fromUser) {
        const v = this.variant;
        const sub = this.plan === 'sub' && v.plans && Object.keys(v.plans).length > 0;
        const planId = sub ? this.frequency || Object.keys(v.plans)[0] : '';
        const planPrice = sub && v.plans[planId] ? v.plans[planId] : null;

        this.dataset.plan = sub ? 'sub' : 'once';
        this.idInput.value = v.id;
        this.planInput.value = planId;
        this.planInput.disabled = !planId;

        this.querySelectorAll('[data-sf-once-price]').forEach((el) => (el.textContent = v.price));
        this.querySelectorAll('[data-sf-sub-price]').forEach((el) => {
          const p = v.plans && (v.plans[this.frequency] || v.plans[Object.keys(v.plans)[0]]);
          el.textContent = p ? p.price : v.price;
        });
        this.querySelectorAll('[data-sf-total]').forEach((el) => (el.textContent = planPrice ? planPrice.price : v.price));

        this.button.disabled = !v.available;
        this.querySelectorAll('[data-sf-label]').forEach((el) => {
          el.hidden = el.dataset.sfLabel !== (v.available ? 'add' : 'soldout');
        });

        if (this.stage) {
          const n = v.count;
          if (fromUser && String(n) !== this.stage.dataset.count && !reduceMotion()) {
            this.stage.removeAttribute('data-anim');
            void this.stage.offsetWidth;
            this.stage.setAttribute('data-anim', '');
          }
          this.stage.dataset.count = n;
          this.stage.querySelectorAll('.sf-stage__bottle').forEach((b, i) => (b.hidden = i >= n));
          const nEl = this.stage.querySelector('[data-sf-stage-n]');
          const uEl = this.stage.querySelector('[data-sf-stage-unit]');
          if (nEl) nEl.textContent = n;
          if (uEl) uEl.textContent = n === 1 ? uEl.dataset.one : uEl.dataset.many;
        }

        if (fromUser) this.setStatus('');
      }

      setStatus(text, error) {
        if (!this.status) return;
        this.status.textContent = text;
        this.status.toggleAttribute('data-error', !!error);
      }

      async add(e) {
        e.preventDefault();
        if (this.button.disabled) return;
        const item = { id: Number(this.idInput.value), quantity: 1 };
        if (!this.planInput.disabled && this.planInput.value) item.selling_plan = Number(this.planInput.value);

        this.button.disabled = true;
        this.button.setAttribute('aria-busy', 'true');
        try {
          const res = await fetch(`${(window.Shopify && Shopify.routes && Shopify.routes.root) || '/'}cart/add.js`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ items: [item] }),
          });
          const data = await res.json();
          if (!res.ok || data.status) throw new Error(data.description || data.message || '');

          const drawer = window.theme && theme.settings && theme.settings.cartType === 'modal';
          if (drawer) {
            this.setStatus(this.dataset.addedText || 'Added to your cart.');
            document.dispatchEvent(new CustomEvent('theme:update:cart'));
            setTimeout(() => document.dispatchEvent(new CustomEvent('theme:open:cart-drawer')), 250);
          } else {
            window.location.href = `${(window.Shopify && Shopify.routes && Shopify.routes.root) || '/'}cart`;
          }
        } catch (err) {
          this.setStatus(err.message || this.dataset.errorText || 'Could not add to cart. Please try again.', true);
        } finally {
          this.button.removeAttribute('aria-busy');
          this.button.disabled = !this.variant.available;
        }
      }
    }
  );
}

if (!customElements.get('sf-see-it')) {
  customElements.define(
    'sf-see-it',
    class extends HTMLElement {
      connectedCallback() {
        if (this._ready) return;
        this._ready = true;
        this.section = this.closest('.sf-see') || this;
        this.video = this.querySelector('video');
        this.chapters = [...this.querySelectorAll('[data-sf-ch]')];
        if (!this.video || !this.chapters.length) return;

        this.starts = this.chapters.map((c) => parseFloat(c.dataset.start) || 0);
        this.userPaused = false;
        this.inView = false;
        this.current = -1;

        this.chapters.forEach((c, i) => c.addEventListener('click', () => this.seek(i)));
        this.querySelector('[data-sf-toggle]')?.addEventListener('click', () => this.toggle());
        this.querySelector('[data-sf-sound]')?.addEventListener('click', () => this.sound());

        this.video.addEventListener('play', () => {
          this.section.setAttribute('data-playing', '');
          this.loop();
        });
        this.video.addEventListener('pause', () => this.section.removeAttribute('data-playing'));
        this.video.addEventListener('timeupdate', () => this.sync());
        this.video.addEventListener('loadedmetadata', () => this.sync());
        this.section.toggleAttribute('data-muted', this.video.muted);

        this.setActive(0, 0);

        if ('IntersectionObserver' in window) {
          this.io = new IntersectionObserver(
            (entries) => {
              const entry = entries[entries.length - 1];
              this.inView = entry.isIntersecting;
              if (this.inView) {
                this.load();
                if (!this.userPaused && !reduceMotion()) this.play();
              } else if (!this.video.paused) {
                this.video.pause();
              }
            },
            { threshold: 0.4 }
          );
          this.io.observe(this.video);
        } else {
          this.load();
        }
      }

      disconnectedCallback() {
        if (this.io) this.io.disconnect();
        cancelAnimationFrame(this.raf);
      }

      load() {
        if (this.video.dataset.loaded) return;
        const v = this.video;
        const small = window.matchMedia('(max-width: 749px)').matches;
        const src = (small && v.dataset.srcSm) || v.dataset.srcLg || v.dataset.srcSm;
        if (!src) return;
        v.src = src;
        v.dataset.loaded = '1';
      }

      play() {
        this.load();
        const p = this.video.play();
        if (p && p.catch) p.catch(() => {});
      }

      toggle() {
        if (this.video.paused) {
          this.userPaused = false;
          this.play();
        } else {
          this.userPaused = true;
          this.video.pause();
        }
      }

      sound() {
        this.video.muted = !this.video.muted;
        this.section.toggleAttribute('data-muted', this.video.muted);
        if (!this.video.muted && this.video.paused) {
          this.userPaused = false;
          this.play();
        }
      }

      seek(i) {
        this.load();
        const go = () => {
          try {
            this.video.currentTime = this.starts[i] + 0.05;
          } catch (e) {
            /* metadata not ready yet; loadedmetadata will sync */
          }
        };
        if (this.video.readyState >= 1) go();
        else this.video.addEventListener('loadedmetadata', go, { once: true });
        this.setActive(i, 0);
        this.userPaused = false;
        this.play();
      }

      indexAt(t) {
        let idx = 0;
        this.starts.forEach((s, i) => {
          if (t >= s) idx = i;
        });
        return idx;
      }

      sync() {
        const t = this.video.currentTime || 0;
        const d = this.video.duration || 0;
        const i = this.indexAt(t);
        const start = this.starts[i];
        const end = i + 1 < this.starts.length ? this.starts[i + 1] : d || start + 1;
        const p = end > start ? Math.min(1, Math.max(0, (t - start) / (end - start))) : 0;
        this.setActive(i, p);
      }

      loop() {
        cancelAnimationFrame(this.raf);
        const step = () => {
          if (this.video.paused) return;
          this.sync();
          this.raf = requestAnimationFrame(step);
        };
        this.raf = requestAnimationFrame(step);
      }

      setActive(i, p) {
        if (i !== this.current) {
          this.chapters.forEach((c, k) => c.setAttribute('aria-current', k === i ? 'true' : 'false'));
          this.current = i;
        }
        this.chapters[i].style.setProperty('--p', p.toFixed(4));
      }
    }
  );
}
